import { useState, useEffect, useCallback } from 'react';
import ReactModernDrawer from 'react-modern-drawer';
import { useDrag } from '@use-gesture/react';
import { cn } from '@/service/utils/cn';
import { IconButton } from '../Buttons/IconButton';
import { ArrowBack } from '../Icons/ArrowBack';
import { CloseIcon } from '../Icons/CloseIcon';
import { ConfirmationPopup } from '../Popups/ConfirmationPopup';
import 'react-modern-drawer/dist/index.css';

type TReactModernDrawerProps = {
    open?: boolean;
    onClose?: () => void;
    direction?: 'left' | 'right' | 'top' | 'bottom';
    lockBackgroundScroll?: boolean;
    children?: React.ReactNode;
    duration?: number;
    overlayOpacity?: number;
    overlayColor?: string;
    enableOverlay?: boolean;
    style?: React.CSSProperties;
    zIndex?: number;
    size?: number | string;
    className?: string;
    customIdSuffix?: string;
    overlayClassName?: string;
};

type TDrawerProps = TReactModernDrawerProps & {
    wrapperClassName?: string;
    confirmBeforeClosing?: boolean;
    withBackIcon?: boolean;
    draggable?: boolean;
    caption?: React.ReactNode | string;
    fullHeightByDefault?: boolean;
    hideConfirmation?: () => void;
    onConfirm?: () => void;
    onCancel?: () => void;
    handleGoingBack?: () => void;
};

const TOP_POINT = () => (typeof window !== 'undefined' ? window.innerHeight - 75 : 900);
const MIDDLE_POINT = () =>
    typeof window !== 'undefined' ? window.innerHeight - window.innerHeight / 4 : 650;
const BOTTOM_POINT = () => (typeof window !== 'undefined' ? window.innerHeight / 3 : 150);

const INITIAL_SIZE =
    typeof window !== 'undefined' ? window.innerHeight - window.innerHeight / 3 : 500;

const CAPTION_HEIGHT = 54;

export const Drawer = ({
    className,
    wrapperClassName,
    open = false,
    confirmBeforeClosing = false,
    withBackIcon = false,
    draggable = false,
    onClose,
    hideConfirmation,
    onConfirm,
    onCancel,
    handleGoingBack,
    caption,
    children,
    fullHeightByDefault,
    ...props
}: TDrawerProps) => {
    const [drawerSize, setDrawerSize] = useState(fullHeightByDefault ? TOP_POINT() : 0);
    const [isDragging, setIsDragging] = useState(false);

    const handleClose = useCallback(() => {
        if (drawerSize !== INITIAL_SIZE) setDrawerSize(INITIAL_SIZE);
        onClose?.();
    }, [drawerSize, onClose]);

    const handleDrag = useDrag(
        ({ dragging, delta: [, yDelta] }) => {
            if (!draggable) return;
            setIsDragging(dragging as boolean);
            setDrawerSize((pS) => pS - yDelta);
        },
        { axis: 'y' }
    );

    useEffect(() => {
        if (!draggable) return;
        if (isDragging) {
            if (drawerSize >= TOP_POINT()) setDrawerSize(TOP_POINT());
            if (drawerSize <= BOTTOM_POINT()) setDrawerSize(BOTTOM_POINT());
        } else {
            if (drawerSize >= MIDDLE_POINT()) {
                setDrawerSize(TOP_POINT());
            } else if (drawerSize && drawerSize <= BOTTOM_POINT()) {
                handleClose();
            } else {
                setDrawerSize(INITIAL_SIZE);
            }
        }
    }, [draggable, isDragging, drawerSize, handleClose]);

    return (
        <ReactModernDrawer
            open={open}
            onClose={handleClose}
            direction="bottom"
            className={cn(
                'z-50 mx-auto max-h-full max-w-[810px] overflow-hidden rounded-t-[15px] bg-[#ffffff!important] dark:bg-[#293847!important]',
                className
            )}
            duration={isDragging ? 0 : 150}
            size={draggable ? drawerSize : 'max-content'}
            {...props}
        >
            <div className="h-full w-full">
                <div
                    className="custom-scroll h-max max-h-full w-full overflow-y-auto overflow-x-hidden"
                    id="tickets-container"
                >
                    {caption && (
                        <div
                            className="sticky left-0 top-0 z-50 h-[54px] w-full cursor-default touch-none bg-white dark:bg-dark-3"
                            {...handleDrag()}
                        >
                            {withBackIcon && (
                                <IconButton
                                    icon={
                                        <ArrowBack className="h-6 w-6 [&_path]:dark:fill-white" />
                                    }
                                    className="absolute left-[15px] top-4 z-50 rounded-full md:left-[22px] md:top-8 lg:left-[25px] lg:top-[34px]"
                                    onClick={handleGoingBack}
                                />
                            )}
                            <div
                                className={`absolute left-1/2 top-2.5 z-50 h-[5px] w-[38px] -translate-x-1/2 rounded-[15px] bg-gray-4 dark:bg-blue-1 ${
                                    draggable ? 'block' : 'hidden'
                                }`}
                            />
                            <div className="absolute left-1/2 top-[15px] z-30 h-max w-full -translate-x-1/2 bg-white pb-2.5 pt-7 dark:bg-dark-3 md:pt-3 lg:pt-2.5">
                                {caption}
                            </div>
                            <IconButton
                                icon={<CloseIcon className="h-10 w-10 md:h-9 md:w-9" />}
                                className="absolute right-[15px] top-[6px] z-50 rounded-full md:right-5 md:top-[21px] lg:right-[17px] lg:top-[25px]"
                                onClick={handleClose}
                            />
                        </div>
                    )}
                    <div
                        className={cn(
                            `${draggable ? 'max-h-full' : 'max-h-[calc(100vh_-_160px)]'}`,
                            wrapperClassName
                        )}
                        style={{
                            height: `${(drawerSize - CAPTION_HEIGHT).toFixed()}px`,
                        }}
                    >
                        {children}
                    </div>
                </div>
            </div>
            <ConfirmationPopup
                confirmText="Leave"
                cancelText="Stay"
                questionText="Are you sure you want to close?"
                infoText="You will lose your progress"
                isShown={confirmBeforeClosing}
                onHide={hideConfirmation}
                onConfirm={onConfirm}
                onCancel={onCancel}
            />
        </ReactModernDrawer>
    );
};
